import React, { useEffect, useState } from "react";
import { supabase } from "../../../utils/supabase";

// ✅ Iconos PRO
import { Search, QrCode, PencilLine, Loader2, Trash2 } from "lucide-react";

// ✅ UI PRO reusable (crealos en src/components/ui)
import Spinner from "../../../components/ui/Spinner.jsx";
import Skeleton from "../../../components/ui/Skeleton.jsx";
import EmptyState from "../../../components/ui/EmptyState.jsx";

// ✅ Modal de QR (mismo que se usa en Ingresos)
import QRLabelModal from "../../../components/QRLabelModal.jsx";

// ✅ Reutilizamos el editor existente (modo edit)
import IntakeInstrumentModal from "./IntakeInstrumentModal";
import InstrumentDetailModal from "./InstrumentDetailModal";

/* ──────────────────────────────────────────────────────────────
   Helpers sucursal
─────────────────────────────────────────────────────────────── */
function formatBranchLabel(branch_no, branch_name) {
  if (branch_no == null) return "";
  const bn = String(branch_no).trim();
  const bname = (branch_name || "").trim();
  return bname ? `Sucursal ${bn} - ${bname}` : `Sucursal ${bn}`;
}

function formatClientDisplay(c) {
  if (!c) return "";
  const cn = c.client_number ?? "—";
  const bNo = c.branch_no != null ? ` ${c.branch_no}` : "";
  const bLabel = formatBranchLabel(c.branch_no, c.branch_name);
  const cuitLabel = c.cuit ? ` · CUIT ${c.cuit}` : c.tax_id ? ` · ${c.tax_id}` : "";
  const branchExtra = bLabel ? ` · ${bLabel}` : "";
  return `${cn}${bNo} — ${c.name || "—"}${branchExtra}${cuitLabel}`;
}

function getDeleteBlockMessage(error) {
  const code = String(error?.code || "");
  const details = String(error?.details || "");
  const message = String(error?.message || "");
  const combined = `${code} ${details} ${message}`.toLowerCase();

  if (combined.includes("service_prices_instrument_id_fkey") || combined.includes("service_prices")) {
    return "No se puede borrar: este instrumento todavía está referenciado en service_prices. Primero hay que quitar o reasignar esas tarifas.";
  }

  if (code === "23503") {
    return "No se puede borrar: el instrumento todavía tiene registros relacionados en la base.";
  }

  return null;
}

// --- Búsqueda "inteligente" de clientes (similar a CreateIntakeModal) ---
async function searchClientsSmart(q, limit = 20) {
  // ✅ Incluimos sucursales
  const baseSelect = "id,name,client_number,cuit,tax_id,branch_no,branch_name";
  const s = (q ?? "").trim();
  if (!s) return [];

  const digitsOnly = s.replace(/\D/g, "");
  const isAllDigits = /^\d+$/.test(s);
  const isLikelyCuit = /^\d{8,11}$/.test(digitsOnly);

  // Nº de cliente exacto
  if (isAllDigits && !isLikelyCuit) {
    const { data, error } = await supabase
      .from("clients")
      .select(baseSelect)
      .eq("client_number", Number(s))
      .order("branch_no", { ascending: true, nullsFirst: true })
      .order("name", { ascending: true })
      .limit(limit);
    if (error) throw error;
    return data ?? [];
  }

  // CUIT exacto / like
  if (isLikelyCuit) {
    const exact = await supabase
      .from("clients")
      .select(baseSelect)
      .eq("cuit", digitsOnly)
      .order("branch_no", { ascending: true, nullsFirst: true })
      .limit(limit);
    if (exact.error) throw exact.error;
    if ((exact.data ?? []).length) return exact.data;

    const like = await supabase
      .from("clients")
      .select(baseSelect)
      .ilike("cuit", `%${digitsOnly}%`)
      .order("branch_no", { ascending: true, nullsFirst: true })
      .order("name", { ascending: true })
      .limit(limit);
    if (like.error) throw like.error;
    return like.data ?? [];
  }

  // Nombre
  const { data, error } = await supabase
    .from("clients")
    .select(baseSelect)
    .ilike("name", `%${s}%`)
    .order("name", { ascending: true })
    .order("branch_no", { ascending: true, nullsFirst: true })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

function formatVariable(type) {
  const map = {
    pressure: "Presión",
    temperature: "Temperatura",
    electrical: "Eléctrica",
    dimensional: "Dimensional",
    mass: "Masa",
    flow: "Caudal",
    force: "Fuerza/Torque",
    gas: "Gases",
    acoustic: "Sonoro",
    illumination: "Lux",
    air_velocity: "Aire",
    analytical: "Analítica",
    other: "Otro",
  };
  return map[type] || type || "—";
}

function formatInstrumentStatus(status) {
  const map = {
    active: "En depósito",
    in_progress: "En proceso",
    calibrated: "Calibrado",
    rejected: "Rechazado",
    with_certificate: "Con certificado",
    without_certificate: "Sin certificado",
    delivered_without_certificate: "Entregado sin certificado",
    delivered: "Entregado",
  };
  return map[status] || status || "—";
}

export default function InstrumentSearchModal({
  open,
  onClose,
  onOpenQRModal,
  canDelete = true,
  canChangeClient = true,
  criticalUnlocked = false,
  onRequestCriticalAccess,
}) {
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const [q, setQ] = useState(""); // tag / serie / lab_id / marca / modelo / código cliente
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  // ✅ Eliminar
  const [deletingId, setDeletingId] = useState(null);

  // ✅ Editor
  const [editModal, setEditModal] = useState({
    open: false,
    instrumentId: null,
    clientName: "",
  });

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailInstrumentId, setDetailInstrumentId] = useState(null);

  // 🎟️ QR
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrModalInstrument, setQrModalInstrument] = useState(null);
  const [qrModalClientName, setQrModalClientName] = useState("");
  const [qrModalPoCode, setQrModalPoCode] = useState("");

  const openQrForInstrument = (row) => {
    if (!row) return;

    // Si el padre provee handler, lo usamos (compatibilidad)
    if (typeof onOpenQRModal === "function") {
      onOpenQRModal(row);
      return;
    }

    const client = row.client || {};

    setQrModalInstrument({
      id: row.id,
      labId: row.lab_id,
      tag: row.tag,
      // Preferimos el nombre de la categoría (tipo real) si viene
      type: row.category?.name || row.type,
      // No siempre existe en instruments; QRLabelModal sabe generar si no hay URL
      qrImageUrl: row.qr_image_url || row.instrument_qr_url || null,
      poId: null,
    });
    setQrModalClientName(client?.name || "");
    setQrModalPoCode("");
    setQrModalOpen(true);
  };

  const openInstrumentDetails = (instrumentId) => {
    if (!instrumentId) return;
    setDetailInstrumentId(instrumentId);
    setDetailModalOpen(true);
  };

  async function handleDeleteInstrument(row) {
    if (!row?.id) return;

    const label = row.lab_id != null ? `#${row.lab_id}` : row.tag || "sin tag";
    const ok = window.confirm(
      `¿Eliminar instrumento ${label}?\n\nEsta acción NO se puede deshacer.`
    );
    if (!ok) return;

    try {
      setDeletingId(row.id);

      const { error } = await supabase.from("instruments").delete().eq("id", row.id);
      if (error) {
        const friendly = getDeleteBlockMessage(error);
        if (friendly) {
          throw new Error(friendly);
        }
        throw error;
      }

      // refrescar resultados
      await handleSearch();
    } catch (e) {
      console.error("[InstrumentSearchModal] delete error", e);
      alert(e?.message || "No se pudo eliminar el instrumento.");
    } finally {
      setDeletingId(null);
    }
  }

  // Reset al abrir/cerrar
  useEffect(() => {
    if (!open) {
      setClientQuery("");
      setClientResults([]);
      setClientLoading(false);
      setSelectedClient(null);
      setQ("");
      setSearching(false);
      setResults([]);
      setErrorMsg("");
      setDeletingId(null);
      setEditModal({ open: false, instrumentId: null, clientName: "" });
      setDetailModalOpen(false);
      setDetailInstrumentId(null);

      setQrModalOpen(false);
      setQrModalInstrument(null);
      setQrModalClientName("");
      setQrModalPoCode("");
      return;
    }
  }, [open]);

  // Autocomplete de clientes
  useEffect(() => {
    const qq = (clientQuery || "").trim();
    if (!qq || selectedClient) {
      setClientResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setClientLoading(true);
        const rows = await searchClientsSmart(qq, 20);
        setClientResults(rows || []);
      } catch (e) {
        console.error("client search (InstrumentSearchModal)", e);
        setClientResults([]);
      } finally {
        setClientLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [clientQuery, selectedClient]);

  async function handleSearch() {
    try {
      setSearching(true);
      setErrorMsg("");
      setResults([]);

      let query = supabase
        .from("instruments")
        .select(
          `
          id,
          lab_id,
          tag,
          brand,
          model,
          serial,
          type,
          status,
          baker_code,
          created_at,
          category:instrument_categories ( id, name ),
          client:clients ( id, name, client_number, branch_no, branch_name )
        `
        )
        .order("lab_id", { ascending: true })
        .limit(200);

      if (selectedClient?.id) {
        query = query.eq("client_id", selectedClient.id);
      }

      const s = (q || "").trim();
      if (s) {
        const sSafe = s.replace(/,/g, " "); // evitamos romper el string del or
        const isNum = /^\d+$/.test(sSafe);
        const orParts = [
          `tag.ilike.%${sSafe}%`,
          `serial.ilike.%${sSafe}%`,
          `brand.ilike.%${sSafe}%`,
          `model.ilike.%${sSafe}%`,
          `baker_code.ilike.%${sSafe}%`,
        ];
        if (isNum) {
          // busca también por lab_id EXACTO
          orParts.push(`lab_id.eq.${Number(sSafe)}`);
        }
        query = query.or(orParts.join(","));
      }

      const { data, error } = await query;
      if (error) throw error;

      setResults(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("[InstrumentSearchModal] search error", e);
      setErrorMsg(e.message || "No se pudo buscar instrumentos.");
    } finally {
      setSearching(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className="w-full max-w-[96vw] 2xl:max-w-7xl rounded-2xl bg-[#0F1115] text-white border border-white/10 shadow-2xl p-5 max-h-[90vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Buscar instrumentos</h3>
            <p className="text-xs opacity-70">
              Podés filtrar por cliente y/o por Tag, Nº de serie, lab_id, marca,
              modelo o código cliente (Baker).
            </p>
          </div>
          <button
            className="px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/10"
            onClick={onClose}
          >
            Cerrar
          </button>
        </div>

        {/* Selector de cliente */}
        <div className="mb-4">
          <label className="text-sm opacity-80">Cliente (opcional)</label>
          <div className="relative">
            <input
              className="w-full mt-1 mb-2 px-3 py-2 rounded-md bg-white/10 border border-white/10"
              placeholder="Buscar por Nº cliente, nombre o CUIT…"
              value={selectedClient ? formatClientDisplay(selectedClient) : clientQuery}
              onChange={(e) => {
                setSelectedClient(null);
                setClientQuery(e.target.value);
              }}
            />

            {/* Resultados de clientes */}
            {!selectedClient &&
              clientQuery.trim() &&
              (clientLoading || clientResults.length > 0) && (
                <div className="absolute left-0 right-0 z-[90] mt-1 w-full max-h-72 overflow-auto rounded-xl border border-white/10 shadow-2xl bg-[#0F1115]">
                  {clientLoading && (
                    <div className="px-3 py-2 text-sm text-white/70 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Buscando…
                    </div>
                  )}

                  {!clientLoading &&
                    clientResults.map((c) => {
                      const branchLabel = formatBranchLabel(c.branch_no, c.branch_name);
                      const cn = c.client_number ?? "—";
                      const bNo = c.branch_no != null ? ` ${c.branch_no}` : "";

                      return (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-white/10 focus:bg-white/10 focus:outline-none text-sm"
                          onClick={() => {
                            setSelectedClient(c);
                            setClientQuery("");
                            setClientResults([]);
                          }}
                        >
                          <div className="font-medium">
                            {cn}
                            {bNo} — {c.name}
                            {branchLabel ? (
                              <span className="ml-2 text-xs opacity-70">
                                ({branchLabel})
                              </span>
                            ) : null}
                          </div>
                          <div className="text-xs opacity-70">
                            {c.cuit ? `CUIT ${c.cuit}` : c.tax_id || "—"}
                          </div>
                        </button>
                      );
                    })}

                  {!clientLoading && clientResults.length === 0 && (
                    <div className="px-3 py-2 text-sm opacity-70">Sin resultados</div>
                  )}
                </div>
              )}
          </div>

          {selectedClient && (
            <div className="mt-1 text-xs opacity-80">
              Cliente seleccionado: <b>{formatClientDisplay(selectedClient)}</b>{" "}
              <button
                className="ml-2 underline hover:no-underline"
                onClick={() => setSelectedClient(null)}
              >
                Cambiar
              </button>
            </div>
          )}
        </div>

        {/* Campo de búsqueda general */}
        <div className="mb-4">
          <label className="block text-sm mb-1">
            Buscar por Tag / Nº serie / lab_id / marca / modelo / código cliente
          </label>
          <div className="flex gap-2">
            <input
              className="flex-1 px-3 py-2 rounded-md bg-white/10 border border-white/10"
              placeholder="Ej: 401, PT-102, 123456, Rosemount, etc."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />

            <button
              className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 flex items-center gap-2"
              onClick={handleSearch}
              disabled={searching}
            >
              {searching ? (
                <Spinner size={16} label="Buscando…" />
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  Buscar
                </>
              )}
            </button>
          </div>

          <p className="text-xs opacity-70 mt-1">
            Si solo elegís un cliente y dejás vacío el buscador, se listan todos
            los instrumentos de ese cliente (hasta 200).
          </p>
        </div>

        {/* Error */}
        {errorMsg && (
          <div className="mb-3 rounded border border-red-900/40 bg-red-900/10 p-2 text-xs text-red-300">
            {errorMsg}
          </div>
        )}

        {/* Resultados */}
        <div className="rounded-md border border-white/10">
          <div className="bg-white/5 px-3 py-2 text-sm flex items-center justify-between">
            <span>Resultados</span>
            <span className="text-xs opacity-70">
              {results.length} instrumento{results.length === 1 ? "" : "s"} (máx. 200)
            </span>
          </div>

          {searching && (
            <div className="p-3 space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-8" />
              ))}
            </div>
          )}

          {!searching && results.length === 0 && (
            <EmptyState
              title="No hay resultados"
              description="Elegí un cliente o probá buscando por tag / serie / lab_id / marca / modelo."
              action={{ label: "Reintentar", onClick: handleSearch }}
            />
          )}

          {!searching && results.length > 0 && (
            <div className="overflow-x-auto max-h-[45vh]">
              <table className="min-w-full text-xs">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-3 py-2 text-left">Cliente</th>
                    <th className="px-3 py-2 text-left">Lab ID</th>
                    <th className="px-3 py-2 text-left">Tag</th>
                    <th className="px-3 py-2 text-left">Serie</th>
                    <th className="px-3 py-2 text-left">Marca / Modelo</th>
                    <th className="px-3 py-2 text-left">Variable</th>
                    <th className="px-3 py-2 text-left">Tipo instrumento</th>
                    <th className="px-3 py-2 text-left">Código cliente</th>
                    <th className="px-3 py-2 text-left">Estado</th>
                    <th className="px-3 py-2 text-left min-w-[260px]">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => {
                    const client = r.client || {};
                    const categoryName = r.category?.name || "—";
                    const branchLabel = formatBranchLabel(client.branch_no, client.branch_name);
                    const cn = client.client_number ?? "—";
                    const bNo = client.branch_no != null ? ` ${client.branch_no}` : "";

                    return (
                      <tr
                        key={r.id}
                        className="border-t border-white/10 hover:bg-white/5"
                      >
                        <td className="px-3 py-2">
                          {cn}
                          {bNo} — {client.name ?? "—"}
                          {branchLabel ? (
                            <div className="text-[11px] opacity-70">{branchLabel}</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2">{r.lab_id != null ? `#${r.lab_id}` : "—"}</td>
                        <td className="px-3 py-2">{r.tag || "—"}</td>
                        <td className="px-3 py-2">{r.serial || "—"}</td>
                        <td className="px-3 py-2">
                          {(r.brand || "") + (r.brand && r.model ? " " : "") + (r.model || "") || "—"}
                        </td>
                        <td className="px-3 py-2">{formatVariable(r.type)}</td>
                        <td className="px-3 py-2">{categoryName}</td>
                        <td className="px-3 py-2">{r.baker_code || "—"}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 border border-white/20 text-[11px]">
                            {formatInstrumentStatus(r.status || "active")}
                          </span>
                        </td>

                        {/* ✅ Acciones PRO */}
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              className="px-2 py-1 rounded-md border border-white/20 hover:bg-white/10 flex items-center gap-1"
                              title="Generar / ver QR"
                              onClick={() => openQrForInstrument(r)}
                            >
                              <QrCode className="w-4 h-4" />
                              QR
                            </button>

                            <button
                              className="px-2 py-1 rounded-md border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10 flex items-center gap-1"
                              onClick={() =>
                                setEditModal({
                                  open: true,
                                  instrumentId: r.id,
                                  clientName: client?.name || "",
                                })
                              }
                            >
                              <PencilLine className="w-4 h-4" />
                              Editar
                            </button>

                            <button
                              className="px-2 py-1 rounded-md border border-sky-500/40 text-sky-200 hover:bg-sky-500/10 flex items-center gap-1"
                              onClick={() => openInstrumentDetails(r.id)}
                            >
                              <Search className="w-4 h-4" />
                              Ficha
                            </button>

                            {canDelete && (
                              <button
                                className="px-2 py-1 rounded-md border border-red-500/40 text-red-200 hover:bg-red-500/10 flex items-center gap-1 disabled:opacity-60"
                                title="Eliminar instrumento"
                                onClick={() => handleDeleteInstrument(r)}
                                disabled={deletingId === r.id}
                              >
                                <Trash2 className="w-4 h-4" />
                                {deletingId === r.id ? "Eliminando…" : "Eliminar"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-[10px] opacity-60 mt-2">
          Tip: podés usar esto como “identificador rápido” cuando el cliente te
          da solo un tag, serie o código interno y no sabés a qué instrumento
          corresponde.
        </p>

        {/* ✅ Modal editar instrumento */}
        <IntakeInstrumentModal
          open={editModal.open}
          mode="edit"
          instrumentId={editModal.instrumentId}
          client={{ name: editModal.clientName }}
          onClose={() => setEditModal({ open: false, instrumentId: null, clientName: "" })}
          onSaved={async () => {
            setEditModal({ open: false, instrumentId: null, clientName: "" });
            await handleSearch();
          }}
          canChangeClient={canChangeClient}
          criticalUnlocked={criticalUnlocked}
          onRequestCriticalAccess={onRequestCriticalAccess}
        />

        {/* ✅ Modal QR (etiqueta) */}
        <InstrumentDetailModal
          open={detailModalOpen}
          instrumentId={detailInstrumentId}
          onClose={() => {
            setDetailModalOpen(false);
            setDetailInstrumentId(null);
          }}
        />

        {qrModalOpen && qrModalInstrument && (
          <QRLabelModal
            open={qrModalOpen}
            instrument={qrModalInstrument}
            clientName={qrModalClientName}
            poCode={qrModalPoCode}
            onClose={() => {
              setQrModalOpen(false);
              setQrModalInstrument(null);
              setQrModalClientName("");
              setQrModalPoCode("");
            }}
          />
        )}
      </div>
    </div>
  );
}
